import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import customersAPI from '../../utils/api/customers';

function normalizeCustomer(customer) {
  if (!customer) return customer;
  const n = Number(customer.cust_extern);
  return {
    ...customer,
    cust_extern: Number.isFinite(n) ? Math.trunc(n) : 0,
  };
}

function normalizeCustomers(list) {
  return Array.isArray(list) ? list.map(normalizeCustomer) : [];
}

export const fetchCustomers = createAsyncThunk(
  'customers/fetchCustomers',
  async (_, { rejectWithValue }) => {
    try {
        const response = await customersAPI.getAllCustomers();
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const fetchCustomerByNumber = createAsyncThunk(
  'customers/fetchCustomerByNumber',
  async (custNum, { rejectWithValue }) => {
    try {
        const response = await customersAPI.getByNumber(custNum);
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const createCustomer = createAsyncThunk(
  'customers/createCustomer',
  async (customerData, { rejectWithValue }) => {
    try {
        const response = await customersAPI.create(customerData);
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const updateCustomer = createAsyncThunk(
  'customers/updateCustomer',
  async (customerData, { rejectWithValue }) => {
    try {
        const response = await customersAPI.update(customerData.number, customerData);
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const deleteCustomer = createAsyncThunk(
  'customers/deleteCustomer',
  async (custNum, { rejectWithValue }) => {
    try {
        const response = await customersAPI.delete(custNum);
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const searchCustomers = createAsyncThunk(
  'customers/searchCustomers',
  async (query, { rejectWithValue }) => {
    try {
        const response = await customersAPI.search(query);
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const fetchCustomersByChargeDate = createAsyncThunk(
  'customers/fetchCustomersByChargeDate',
  async (date, { rejectWithValue }) => {
    try {
        const response = await customersAPI.getByChargeDate(date);
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});



const initialState = {
    customersList: [],
    selectedCustomer: null,
    searchResults: [],
    customersByChargeDate: [],
    isDialogOpen: false,
    loading: false,
    error: null,
    createLoading: false,
    updateLoading: false,
    deleteLoading: false,
    searchLoading: false,
}

const customersSlice = createSlice({
    name: 'customers',
    initialState,
    reducers: {
        setCustomers: (state, action) => {
            state.customersList = normalizeCustomers(action.payload);
        },
        setSelectedCustomer: (state, action) => {
            state.selectedCustomer = action.payload;
        },
        setSearchResults: (state, action) => {
            state.searchResults = action.payload;
        },
        setCustomersByChargeDate: (state, action) => {
            state.customersByChargeDate = action.payload;
        },
        setIsDialogOpen: (state, action) => {
            state.isDialogOpen = action.payload;
        },
        clearError: (state) => {
            state.error = null;
            state.loading = false;
            state.customersList = [];
        },
        clearCustomersState: (state) => {
            state.customersList = [];
            state.selectedCustomer = null;
            state.searchResults = [];
            state.customersByChargeDate = [];
            state.error = null;
            state.loading = false;
            state.createLoading = false;
            state.updateLoading = false;
            state.deleteLoading = false;
            state.searchLoading = false;
        },
        clearSelectedCustomer: (state) => {
            state.selectedCustomer = null;
        },
        clearSearchResults: (state) => {
            state.searchResults = [];
        },
        clearCustomersByChargeDate: (state) => {
            state.customersByChargeDate = [];
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch all customers
            .addCase(fetchCustomers.pending, (state) => {
                state.loading = true;
                state.selectedCustomer = null;
                state.error = null;
            })
            .addCase(fetchCustomers.fulfilled, (state, action) => {
                state.loading = false;
                state.selectedCustomer = null;
                state.customersList = normalizeCustomers(action.payload.data);
            })
            .addCase(fetchCustomers.rejected, (state, action) => {
                state.loading = false;
                state.selectedCustomer = null;
                state.error = action.payload;
            })
            // Fetch customer by number
            .addCase(fetchCustomerByNumber.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCustomerByNumber.fulfilled, (state, action) => {
                state.loading = false;
                state.selectedCustomer = normalizeCustomer(action.payload.data);
            })
            .addCase(fetchCustomerByNumber.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Create customer
            .addCase(createCustomer.pending, (state) => {
                state.createLoading = true;
                state.error = null;
            })
            .addCase(createCustomer.fulfilled, (state, action) => {
                state.createLoading = false;
                // Add the new customer to the list if it exists
                if (state.customersList && action.payload.data) {
                    state.customersList.unshift(normalizeCustomer(action.payload.data));
                }
            })
            .addCase(createCustomer.rejected, (state, action) => {
                state.createLoading = false;
                state.error = action.payload.message;
            })
            // Update customer
            .addCase(updateCustomer.pending, (state) => {
                state.updateLoading = true;
                state.error = null;
            })
            .addCase(updateCustomer.fulfilled, (state, action) => {
                state.updateLoading = false;
                const updated = normalizeCustomer(action.payload.data);
                // Update the customer in the list if it exists
                if (state.customersList && updated) {
                    const index = state.customersList.findIndex(customer => customer.number === updated.number);
                    if (index !== -1) {
                        state.customersList[index] = updated;
                    }
                }
                // Update selected customer if it's the same one
                if (state.selectedCustomer && updated && state.selectedCustomer.number === updated.number) { 
                    state.selectedCustomer = updated;
                }
            })
            .addCase(updateCustomer.rejected, (state, action) => {
                state.updateLoading = false;
                state.error = action.payload.message;
            })
            // Delete customer
            .addCase(deleteCustomer.pending, (state) => {
                state.deleteLoading = true;
                state.error = null;
            })
            .addCase(deleteCustomer.fulfilled, (state, action) => {
                state.deleteLoading = false;
                // Remove the customer from the list if it exists
                if (state.customersList) {
                    state.customersList = state.customersList.filter(customer => customer.number !== action.meta.arg);
                }
                // Clear selected customer if it's the same one
                if (state.selectedCustomer && state.selectedCustomer.number === action.meta.arg) {
                    state.selectedCustomer = null;
                }
            })
            .addCase(deleteCustomer.rejected, (state, action) => {
                state.deleteLoading = false;
                state.error = action.payload;
            })
            // Search customers
            .addCase(searchCustomers.pending, (state) => {
                state.searchLoading = true;
                state.error = null;
            })
            .addCase(searchCustomers.fulfilled, (state, action) => {
                state.searchLoading = false;
                state.searchResults = normalizeCustomers(action.payload.data);
            })
            .addCase(searchCustomers.rejected, (state, action) => {
                state.searchLoading = false;
                state.error = action.payload;
            })
            // Fetch customers by charge date
            .addCase(fetchCustomersByChargeDate.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCustomersByChargeDate.fulfilled, (state, action) => {
                state.loading = false;
                state.customersByChargeDate = normalizeCustomers(action.payload.data);
            })
            .addCase(fetchCustomersByChargeDate.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

export const { 
    setCustomers, 
    setSelectedCustomer, 
    setSearchResults, 
    setCustomersByChargeDate, 
    setIsDialogOpen, 
    clearError, 
    clearCustomersState, 
    clearSelectedCustomer, 
    clearSearchResults, 
    clearCustomersByChargeDate 
} = customersSlice.actions;
export default customersSlice.reducer;
